import { Alert, Drawer, Empty, Segmented, Space, Statistic, Table, Typography } from "antd";
import dayjs from "dayjs";
import { useState } from "react";

import { type HelpArticleEntity, type HelpArticleKind } from "@fitness-app/data-models/entities/HelpArticle";
import {
  HELP_STATS_PERIODS,
  type HelpArticleReader,
  type HelpStatsPeriod,
} from "@fitness-app/data-models/entities/HelpArticleStats";

import {
  helpStatsKey,
  isHelpStatsUnavailable,
  useHelpArticleReaders,
  useHelpArticleStats,
} from "./useHelpArticleStats";

interface HelpArticleStatsDrawerProps {
  article: HelpArticleEntity;
  open: boolean;
  onClose: () => void;
}

/** TRE-2251 — kto i ile razy otworzył materiał, na podstawie `help_article_viewed`. */
export const HelpArticleStatsDrawer = ({ article, open, onClose }: HelpArticleStatsDrawerProps) => {
  const [days, setDays] = useState<HelpStatsPeriod>(30);
  const ref = { kind: article.kind as HelpArticleKind, slug: article.slug };

  const statsQuery = useHelpArticleStats(days);
  const readersQuery = useHelpArticleReaders(ref, days, open);
  const stats = statsQuery.data?.get(helpStatsKey(ref.kind, ref.slug));

  const error = statsQuery.error ?? readersQuery.error;

  return (
    <Drawer title="Statystyki materiału" width={560} open={open} onClose={onClose} destroyOnClose>
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        <Segmented<HelpStatsPeriod>
          value={days}
          onChange={setDays}
          options={HELP_STATS_PERIODS.map((value) => ({ value, label: `${value} dni` }))}
        />

        {error ? (
          <Alert
            type={isHelpStatsUnavailable(error) ? "info" : "error"}
            message={
              isHelpStatsUnavailable(error)
                ? "Statystyki nie są dostępne w tym środowisku."
                : "Nie udało się pobrać statystyk."
            }
          />
        ) : (
          <>
            <Space size={32} wrap>
              <Statistic title="Otwarcia" value={stats?.views ?? 0} loading={statsQuery.isLoading} />
              <Statistic title="Osoby" value={stats?.visitors ?? 0} loading={statsQuery.isLoading} />
              <Statistic title="Rozpoznane" value={stats?.knownVisitors ?? 0} loading={statsQuery.isLoading} />
              <Statistic title="Z aplikacji" value={stats?.appViews ?? 0} loading={statsQuery.isLoading} />
            </Space>

            <div>
              <Typography.Title level={5}>Kto czytał</Typography.Title>
              <Table<HelpArticleReader>
                size="small"
                rowKey="id"
                loading={readersQuery.isLoading}
                dataSource={readersQuery.data ?? []}
                pagination={{ pageSize: 20, hideOnSinglePage: true }}
                locale={{ emptyText: <Empty description="Brak rozpoznanych osób" /> }}
                columns={[
                  {
                    title: "Osoba",
                    key: "person",
                    render: (_value, reader) => (
                      <Space direction="vertical" size={0}>
                        <Typography.Text>{reader.name ?? reader.email ?? "—"}</Typography.Text>
                        {reader.name && reader.email && (
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {reader.email}
                          </Typography.Text>
                        )}
                      </Space>
                    ),
                  },
                  { title: "Otwarcia", dataIndex: "views", width: 90 },
                  {
                    title: "Ostatnio",
                    dataIndex: "lastViewedAt",
                    width: 130,
                    render: (value: string) => dayjs(value).format("DD.MM.YYYY HH:mm"),
                  },
                ]}
              />
            </div>
          </>
        )}
      </Space>
    </Drawer>
  );
};
